import { useState, useEffect, useContext, useCallback } from 'react';
import { Progress } from '@backstage/core-components';
import {
  DevHubBox,
  DevHubCard,
  DevHubCircularProgress,
  DevHubGrid,
  DevHubTypography,
  DevHubSelect,
  DevHubMenuItem,
} from '@devxdiscover/devhub-ui';
import { IdeaCard } from './IdeaCard';
import { OnboardIdeaButton } from './OnboardIdeaButton';
import {
  ExperimentalApplication,
  IdeaFromAPIResponse,
  mapIdeaToExperimentalApplication,
} from './types';
import { UserContext } from '../../../contexts/UserContext';
import { discoveryApiRef, useApi } from '@backstage/core-plugin-api';
import { deletionStateManager } from './deletionStateManager';

/**
 * MyIdeasView component displays all experimental ideas in a grid layout
 * for the Action Center tab view.
 */
export const MyIdeasView = () => {
  const [experimentalApps, setExperimentalApps] = useState<
    ExperimentalApplication[]
  >([]);
  const [refreshingAppId, setRefreshingAppId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'date' | 'name'>('date');
  const { login } = useContext(UserContext);
  const discoveryApi = useApi(discoveryApiRef);

  // Fetch experimental apps from backend
  const fetchExperimentalApps = useCallback(async () => {
    if (!login) return;

    setLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('experimental-apps');
      const url = `${baseUrl}?userLogin=${login}`;
      // eslint-disable-next-line no-console
      console.log('MyIdeasView: Fetching apps from:', url);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch apps: ${response.statusText}`);
      }

      const ideas: IdeaFromAPIResponse = await response.json();
      // eslint-disable-next-line no-console
      console.log('MyIdeasView: Received ideas:', ideas);

      // Map the new API format to ExperimentalApplication format
      const mappedApps = ideas.applications.map(
        mapIdeaToExperimentalApplication,
      );
      // eslint-disable-next-line no-console
      console.log('MyIdeasView: Mapped apps:', mappedApps);

      setExperimentalApps(mappedApps);

      // Clean up stale deletion states (apps that were deleted outside this session)
      const appIds = mappedApps.map(app => app.applicationId);
      // eslint-disable-next-line no-console
      console.log(
        'MyIdeasView: Calling cleanupStaleStates with app IDs:',
        appIds,
      );
      if ('cleanupStaleStates' in deletionStateManager) {
        (deletionStateManager as any).cleanupStaleStates(appIds);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching experimental apps:', error);
      setExperimentalApps([]);
    } finally {
      setLoading(false);
    }
  }, [login, discoveryApi]);

  // Fetch apps on mount
  useEffect(() => {
    fetchExperimentalApps();
  }, [fetchExperimentalApps]);

  // Listen for successful deletions and refetch
  useEffect(() => {
    const handleAppDeleted = () => {
      // eslint-disable-next-line no-console
      console.log('MyIdeasView: App deleted, refetching all apps');
      fetchExperimentalApps();
    };

    window.addEventListener('app-deleted', handleAppDeleted);
    return () => {
      window.removeEventListener('app-deleted', handleAppDeleted);
    };
  }, [fetchExperimentalApps]);

  const handleRefreshSingleIdea = async (applicationId: number) => {
    if (!login) return;

    setRefreshingAppId(applicationId);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('experimental-apps');
      const url = `${baseUrl}?userLogin=${login}`;
      // eslint-disable-next-line no-console
      console.log('MyIdeasView: Refreshing single app:', applicationId);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch apps: ${response.statusText}`);
      }

      const ideas: IdeaFromAPIResponse = await response.json();
      // eslint-disable-next-line no-console
      console.log('MyIdeasView: Received ideas for refresh');

      // Map all apps
      const mappedApps = ideas.applications.map(
        mapIdeaToExperimentalApplication,
      );

      // Find the specific app and update only that one in state
      const updatedApp = mappedApps.find(
        app => app.applicationId === applicationId,
      );

      if (updatedApp) {
        setExperimentalApps(prev =>
          prev.map(app => {
            if (app.applicationId === applicationId) {
              // Preserve original timestamps to maintain sort order
              return {
                ...updatedApp,
                createdAt: app.createdAt,
                updatedAt: new Date().toISOString(),
              };
            }
            return app;
          }),
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error refreshing single app:', error);
    } finally {
      setRefreshingAppId(null);
    }
  };

  // Sort ideas by selected criteria
  const sortedIdeas = [...experimentalApps].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }
    // Sort by name (A-Z)
    return a.name.localeCompare(b.name);
  });

  const sortBar = (
    <DevHubBox
      display="flex"
      style={{
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
      }}
    >
      <DevHubTypography variant="h5">My Ideas</DevHubTypography>
      <DevHubBox display="flex" style={{ alignItems: 'center', gap: '8px' }}>
        <DevHubTypography variant="body2">Sort by:</DevHubTypography>
        <DevHubSelect
          value={sortBy}
          onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
            setSortBy(e.target.value as 'date' | 'name');
          }}
          style={{ minWidth: '150px' }}
          SelectDisplayProps={{ 'aria-label': 'Sort ideas by' }}
        >
          <DevHubMenuItem value="date">Newest First</DevHubMenuItem>
          <DevHubMenuItem value="name">Name (A-Z)</DevHubMenuItem>
        </DevHubSelect>
        <OnboardIdeaButton onSuccess={fetchExperimentalApps} size="large" />
      </DevHubBox>
    </DevHubBox>
  );

  const body = (
    <DevHubBox padding="medium">
      {/* Background refresh indicator */}
      {loading && sortedIdeas.length > 0 && <Progress />}

      {sortBar}

      {/* Loading State - only show if we have no apps yet */}
      {loading && sortedIdeas.length === 0 && (
        <DevHubBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          padding="large"
        >
          <DevHubCircularProgress />
        </DevHubBox>
      )}

      {/* Empty State */}
      {!loading && sortedIdeas.length === 0 && (
        <DevHubBox padding="large" textAlign="center">
          <DevHubTypography variant="body1" color="textSecondary">
            No ideas onboarded yet. Click "Onboard an Idea" to get started with
            your first innovation project!
          </DevHubTypography>
        </DevHubBox>
      )}

      {/* Ideas Grid */}
      {sortedIdeas.length > 0 && (
        <DevHubGrid container spacing={3}>
          {sortedIdeas.map(idea => (
            <DevHubGrid item xs={12} md={6} lg={4} key={idea.applicationId}>
              <IdeaCard
                idea={idea}
                onRefresh={handleRefreshSingleIdea}
                refreshing={refreshingAppId === idea.applicationId}
              />
            </DevHubGrid>
          ))}
        </DevHubGrid>
      )}
    </DevHubBox>
  );

  return <DevHubCard body={body} />;
};
